import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../../../utils/helpers/ApiClient';
import {
  useGetPhdStudyFieldsQuery,
  useGetPhdWorkingGroupsQuery,
} from '../../../../../redux/services/phd';
import { AppBarCMS } from '../../../../basic';
import { CollapseBlock, Spinner } from '../../../../common';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import {
  normalizeCollection,
  resolvePersonLabel,
} from '../PhdStudyFields/phdStudyFieldsHelpers.js';
import { renderPhdAbbreviatedValue } from '../utils/renderPhdAbbreviatedValue.jsx';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';

const PhdWorkingGroupsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageTitle = t('Pracovné skupiny');
  const { data: groups, isLoading: isGroupsLoading } =
    useGetPhdWorkingGroupsQuery();
  const { data: studyFields, isLoading: isFieldsLoading } =
    useGetPhdStudyFieldsQuery();

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const rows = useMemo(() => {
    const groupsByFieldId = new Map(
      normalizeCollection(groups).map((group) => [
        group.phd_study_field_id,
        group,
      ]),
    );

    return normalizeCollection(studyFields)
      .map((field) => {
        const group = groupsByFieldId.get(field.id);

        return {
          id: field.id,
          study_field: field.study_field,
          code: field.code,
          chair_name: group?.chair ? resolvePersonLabel(group.chair) : '—',
          member_names:
            (group?.members || []).map(resolvePersonLabel).join(', ') || '—',
        };
      })
      .sort((left, right) =>
        left.study_field.localeCompare(right.study_field, 'sk'),
      );
  }, [groups, studyFields]);

  const columns = [
    {
      title: t('Odbor'),
      dataIndex: 'code',
      key: 'study_field',
      render: (value, row) => renderPhdAbbreviatedValue(value, row.study_field),
    },
    {
      title: t('Predseda'),
      dataIndex: 'chair_name',
      key: 'chair_name',
    },
    {
      title: t('Členovia'),
      dataIndex: 'member_names',
      key: 'member_names',
    },
  ];

  if (isGroupsLoading || isFieldsLoading) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdTheses}
        backUrlTooltip={t('Dizertačné témy')}
        permissionsBasic="phd-working-groups"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú pracovné skupiny?')}
          description={t(
            'Každý odbor môže mať jednu pracovnú skupinu. Otvorte riadok odboru a priraďte predsedu a členov, ktorí posudzujú témy daného odboru.',
          )}
        />
        <CollapseBlock>
          <DataGrid
            locale={{
              emptyText: t(
                'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
              ),
            }}
            rowKey={(row) => row.id}
            columns={columns}
            dataSource={rows}
            permissionsBasic="phd-working-groups"
            uniqueTableId="phd-working-groups-table"
            onEditClick={(row) =>
              navigate(`${routes.cmsPhdWorkingGroupsEdit}/${row.id}`)
            }
            onRowClick={(row) =>
              navigate(`${routes.cmsPhdWorkingGroupsEdit}/${row.id}`)
            }
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default PhdWorkingGroupsPage;
